<script lang="ts" setup>
import type { BlogCollectionItem } from '@nuxt/content';

import type { ProductType } from '~/api/common';
import abbreviationLogo from '~/assets/icons/logo/abbreviation.svg?url';
import nameLogo from '~/assets/icons/logo/name.svg?url';

const TEST_ID = 'catalog-page';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const commonStore = useCommonStore();

const { data: articles } = await useAsyncData('product-articles', () =>
  queryCollection('blog')
    .order('order', 'ASC')
    .select('path', 'title', 'image', 'category')
    .where('section_catalog', '=', 1)
    .all(),
);

const selectedCategories = computed<string[]>(() => {
  if (!route.query.categories) return [];
  if (typeof route.query.categories === 'string')
    return route.query.categories.split(',');
  return (route.query.categories || []) as string[];
});

const filteredProducts = computed(() => {
  const categories = commonStore.sections.filter((el) =>
    selectedCategories.value.includes(el.code),
  );
  return commonStore.products.filter(
    (el) =>
      !selectedCategories.value.length ||
      el.sectionIds.some((s) => categories.some((c) => c.id === s)),
  );
});

const productContainerKey = computed(() =>
  filteredProducts.value
    ?.map((el) => el.id)
    .sort()
    .join('_'),
);
const categorySEO = computed(
  () =>
    (selectedCategories.value.length === 1 &&
      commonStore.sections.find((el) => selectedCategories.value[0] === el.code)
        ?.seo) ||
    null,
);

type ArticleItemWithScoreType = {
  data: Pick<BlogCollectionItem, 'path' | 'title' | 'image'>;
  type: 'article';
  score: number;
};
type ProductItemWithScoreType = {
  data: ProductType;
  type: 'product';
  score: number;
};

const categories = computed(() => [
  { value: 'all', label: t('category_all'), default: true },
  ...commonStore.sections.map((el) => ({ value: el.code, label: el.name })),
]);
const list = computed(() => {
  const catalogWithScore: ProductItemWithScoreType[] =
    filteredProducts.value.map((el, i) => ({
      data: el,
      type: 'product',
      score: (i + 1) / (filteredProducts.value.length + 1),
    }));

  if (selectedCategories.value.length || !filteredProducts.value.length)
    return catalogWithScore;

  const articlesWithScore: ArticleItemWithScoreType[] = (
    articles.value || []
  ).map((el, i) => ({
    data: el,
    type: 'article',
    score: (i + 1) / ((articles.value || []).length + 1),
  }));

  const result: (ArticleItemWithScoreType | ProductItemWithScoreType)[] = [];
  let productIndex = 0;
  while (catalogWithScore.length || articlesWithScore.length) {
    if (
      catalogWithScore[0] &&
      (!articlesWithScore[0] ||
        catalogWithScore[0].score < articlesWithScore[0].score ||
        productIndex % 2 === 1)
    ) {
      const item = catalogWithScore.shift();
      if (item) result.push(item);
      productIndex += 1;
    } else {
      const item = articlesWithScore.shift();
      if (item) result.push(item);
    }
  }
  return result;
});

const onChangeCategories = (value: string[]) => {
  const categories =
    value[value.length - 1] === 'all'
      ? []
      : value.filter((el) => el !== 'all').sort();
  router.push({ query: { categories: categories.join(',') || undefined } });
};
</script>

<template>
  <main class="catalogPage" :data-test-id="TEST_ID">
    <Head>
      <Title>{{ categorySEO?.title || t('title') }}</Title>
      <Meta
        v-if="categorySEO?.description"
        name="description"
        :content="categorySEO.description"
      />
      <Meta
        v-if="categorySEO?.keywords"
        name="keywords"
        :content="categorySEO.keywords"
      />
      <Meta property="og:title" :content="categorySEO?.title || t('title')" />
      <Meta
        v-if="categorySEO?.description"
        property="og:description"
        :content="categorySEO.description"
      />
    </Head>

    <div class="catalogPage__header">
      <div class="catalogPage__logoContainer">
        <NuxtLink to="/">
          <picture>
            <source :srcset="nameLogo" media="(min-width: 1240px)" />
            <img
              :src="abbreviationLogo"
              alt="Don't Touch My Skin"
              class="catalogPage__logo"
            />
          </picture>
        </NuxtLink>
      </div>
      <div class="catalogPage__horizontalScroll">
        <BaseToggleGroup
          @update:value="onChangeCategories"
          :value="selectedCategories"
          :data="categories"
          class="catalogPage__categories"
          data-test-id="catalog-filter"
        />
      </div>
    </div>

    <Transition name="catalog-transition" mode="out-in">
      <div
        v-if="list.length"
        :key="productContainerKey"
        class="catalogPage__grid"
      >
        <template v-for="item in list">
          <ProductCard
            v-if="item.type === 'product'"
            :key="`${item.type}_${item.data.id}`"
            :product="item.data"
            :data-test-id="`${TEST_ID}-product-card`"
          />
          <ProductBlogCard
            v-else-if="item.type === 'article'"
            :key="`${item.type}_${item.data.path}`"
            :article="item.data"
            with-badge
            class="catalogPage__blogCard"
          />
        </template>
      </div>
      <div v-else class="catalogPage__notFound">{{ t('empty') }}</div>
    </Transition>

    <SectionSubscribe />
  </main>
</template>

<i18n>
ru:
  title: Каталог товаров
  empty: Продуктов не найдено
  category_all: Все продукты

en:
  title: Products catalog
  empty: No products found
  category_all: All products
</i18n>

<style lang="scss">
.catalogPage {
  &__header {
    column-gap: 18px;
    display: grid;
    grid-template-columns: 1fr 2fr 272px;
    grid-auto-rows: min-content;
    padding: 30px 20px 0;
    overflow: hidden;

    @include mq('lg') {
      display: flex;
      flex-direction: column;
      padding: 25px 0 0;
    }
  }

  &__logoContainer {
    padding: 0 16px;
  }

  &__logo {
    width: 161px;
    filter: invert(1);

    @include mq('lg') {
      width: 188px;
      filter: initial;
    }

    @include mq('md') {
      width: 121px;
    }
  }

  &__horizontalScroll {
    @include mq('lg') {
      margin: 28px 0 0;
      overflow: auto;
    }
  }

  &__categories {
    @include mq('lg') {
      padding: 0 16px;
      width: fit-content;
    }
  }

  &__content {
    padding-top: 40px;

    @include mq('md') {
      padding-top: 26px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 18px;
    row-gap: 40px;
    padding: 72px 20px 126px;
    position: relative;

    @include mq('xl') {
      grid-template-columns: repeat(3, 1fr);
    }

    @include mq('md') {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mq('sm') {
      padding: 42px 16px 40px;
      column-gap: 10px;
      row-gap: 44px;
    }
  }

  &__blogCard {
    @include mq('sm') {
      grid-column: span 2;
    }
  }

  &__notFound {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #121212;
    text-align: center;
    padding: 52px 0;

    @include mq('sm') {
      font-size: 20px;
      line-height: 28px;
      padding: 32px 0;
    }
  }
}

.catalog-transition-enter-active,
.catalog-transition-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.catalog-transition-enter-from,
.catalog-transition-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
